import { useState, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, Users, Plus, Sparkles, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useClientes, useCreateCliente } from "@/hooks/useClientes";
import { useSimilarPartners } from "@/hooks/useSimilarPartners";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { normalizePartnerName, DUPLICATE_NAME_ERROR } from "@/lib/normalizeName";

interface Props {
  value: string;
  onValueChange: (value: string, cliente?: { id: string; nombre: string }) => void;
  className?: string;
  placeholder?: string;
  required?: boolean;
  allowCreate?: boolean;
  /** Highlight the trigger as invalid (e.g. required-but-empty) */
  invalid?: boolean;
  /**
   * Texto inicial que se pre-rellena en el buscador (p. ej. el "receptor" OCR del documento).
   * Reduce fricción cuando la IA no logró resolver el cliente automáticamente.
   */
  defaultSearch?: string;
}

/**
 * Strict combobox for selecting a Cliente from the org master directory.
 * Returns the cliente_id (UUID). Use `required` to render an asterisk.
 *
 * Anti-duplicate shield: normalized filter, smart "¿Quisiste decir…?" suggestions
 * via find_similar_clientes RPC, and DUPLICATE_NAME error handling.
 */
export function ClienteSelectField({
  value,
  onValueChange,
  className,
  placeholder = "Seleccionar cliente…",
  required = false,
  allowCreate = true,
  invalid = false,
  defaultSearch = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState(defaultSearch);
  const { data: clientes = [], isLoading } = useClientes();
  const createCliente = useCreateCliente();
  const { toast } = useToast();

  const [newNombre, setNewNombre] = useState("");
  const [newNit, setNewNit] = useState("");

  const selected = clientes.find((c: any) => c.id === value);

  useEffect(() => {
    if (defaultSearch) setSearch(defaultSearch);
  }, [defaultSearch]);

  // ── Capa 3: filtro UI flexibilizado ────────────────────────────────
  const filteredClientes = useMemo(() => {
    const q = normalizePartnerName(search);
    if (!q) return clientes;
    return clientes.filter((c: any) => {
      const haystack = `${normalizePartnerName(c.nombre)} ${(c.nit ?? "").toLowerCase()}`;
      return haystack.includes(q);
    });
  }, [clientes, search]);

  // ── Capa 4: smart suggestions vía RPC find_similar_clientes ───────
  const showEmpty = filteredClientes.length === 0 && !isLoading;
  const { data: similar, loading: loadingSimilar } = useSimilarPartners(
    "cliente",
    search,
    showEmpty,
  );
  const strongSuggestions = similar.filter((s) => s.similarity_score >= 0.6);

  const openCreateWithPrefill = () => {
    setNewNombre(search.trim() || defaultSearch.trim() || "");
    setNewNit("");
    setOpen(false);
    setCreateOpen(true);
  };

  const linkExisting = (id: string, nombre: string) => {
    onValueChange(id, { id, nombre });
    setOpen(false);
    setSearch("");
  };

  const handleCreate = async () => {
    if (!newNombre.trim()) {
      toast({ title: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    try {
      const created = await createCliente.mutateAsync({
        nombre: newNombre.trim(),
        nit: newNit.trim() || undefined,
      });
      toast({ title: "✅ Cliente creado" });
      setNewNombre("");
      setNewNit("");
      setCreateOpen(false);
      if (created?.id) {
        onValueChange(created.id, { id: created.id, nombre: created.nombre });
      }
    } catch (err: any) {
      if (err?.message === DUPLICATE_NAME_ERROR || err?.code === "DUPLICATE_NAME") {
        const existing = err?.existing;
        toast({
          title: "Ya existe un cliente con este nombre",
          description: existing
            ? `"${existing.nombre}" ya está en el directorio. Vinculándolo automáticamente…`
            : "Revisa el directorio antes de crear un duplicado.",
          variant: "destructive",
        });
        if (existing?.id) {
          onValueChange(existing.id, { id: existing.id, nombre: existing.nombre });
          setCreateOpen(false);
        }
        return;
      }
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full max-w-full min-w-0 justify-between font-normal overflow-hidden",
              invalid && "border-destructive ring-2 ring-destructive/30",
              className,
            )}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="block truncate min-w-0 flex-1 text-left">
                {selected ? selected.nombre : (
                  <span className="text-muted-foreground block truncate">
                    {defaultSearch && !value ? `“${defaultSearch}” — sin vincular` : `${placeholder}${required ? " *" : ""}`}
                  </span>
                )}
              </span>
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar por nombre o NIT…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {showEmpty && (
                <CommandEmpty>
                  <div className="py-4 px-3 text-left space-y-3">
                    {loadingSimilar ? (
                      <p className="text-sm text-muted-foreground text-center">Buscando coincidencias similares…</p>
                    ) : strongSuggestions.length > 0 ? (
                      <>
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <Sparkles className="h-4 w-4 shrink-0" />
                          <p className="text-sm font-medium">¿Quisiste decir…?</p>
                        </div>
                        <div className="space-y-1.5">
                          {strongSuggestions.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => linkExisting(s.id, s.nombre)}
                              className="w-full flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-left text-sm hover:bg-amber-500/10 transition"
                            >
                              <span className="truncate">{s.nombre}</span>
                              <span className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 shrink-0">
                                <Link2 className="h-3 w-3" />
                                Vincular
                              </span>
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground pt-1">
                          Si ninguno coincide, puedes crear uno nuevo:
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center">
                        No se encontraron clientes que coincidan.
                      </p>
                    )}
                    {allowCreate && (
                      <Button
                        type="button"
                        variant={strongSuggestions.length > 0 ? "ghost" : "outline"}
                        size="sm"
                        onClick={openCreateWithPrefill}
                        className="w-full gap-2"
                      >
                        <Plus className="h-4 w-4 text-primary" />
                        <span className="truncate">
                          Crear {search.trim() ? `"${search.trim()}"` : "nuevo cliente"}
                        </span>
                      </Button>
                    )}
                  </div>
                </CommandEmpty>
              )}
              <CommandGroup>
                {filteredClientes.map((c: any) => (
                  <CommandItem
                    key={c.id}
                    value={c.id}
                    onSelect={() => {
                      onValueChange(c.id, { id: c.id, nombre: c.nombre });
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{c.nombre}</span>
                      {c.nit && (
                        <span className="text-[10px] text-muted-foreground">NIT: {c.nit}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              {allowCreate && filteredClientes.length > 0 && (
                <CommandGroup>
                  <CommandItem
                    value="__create__"
                    onSelect={openCreateWithPrefill}
                    className="text-primary"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {search.trim()
                      ? `Crear "${search.trim()}"…`
                      : "Crear nuevo cliente…"}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cli_nombre">Razón Social *</Label>
              <Input
                id="cli_nombre"
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
                placeholder="Importadora ACME S.A.S."
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cli_nit">NIT</Label>
              <Input
                id="cli_nit"
                value={newNit}
                onChange={(e) => setNewNit(e.target.value)}
                placeholder="900.123.456-7"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={createCliente.isPending}>
              {createCliente.isPending ? "Creando…" : "Crear cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
